/**
 * ring-swap API client for the test bench: id-based generation request + poll.
 * Mirrors the existing `api/reviews.ts` pattern (authFetch, `/ring-swap` prefix).
 *
 * The SPA now sends only ids — ring SKU, the loose-gemstone id, the target metal, and (optionally)
 * a products-api image id. The ring-swap API resolves the ring/stone attrs, registers the ring +
 * image, and dedups/queues the job itself; the bench no longer assembles an attrs payload or
 * pre-registers anything.
 */
import type { AuthService } from "../auth/auth-service";
import { config } from "../config";

const base = config.ringSwapPrefix; // "/ring-swap"
const jsonHeaders = { "Content-Type": "application/json" };

async function errorText(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string; message?: string };
    return body.error ?? body.message ?? `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/**
 * The id-based `POST /ring-swap/v1/generations` body. At least one of `stone_sku` / `target_metal`
 * must be present; the API infers the operation. `ring_image_id`/`stone_image_id` are products-api
 * image ids (optional overrides — default is the product's default image). `ring_image_id` XOR
 * `all_images`.
 */
export interface GenerationRequest {
  ring_sku: string;
  stone_sku?: string;
  target_metal?: string;
  ring_image_id?: string;
  stone_image_id?: string;
  all_images?: boolean;
  idempotency_key?: string;
}

/** The normalised result of a generation request — the raw HTTP status + body, for the bench to
 * render faithfully (200 hit/noop, 202 accepted, 409 rejected, 422 invalid, 404 not_found). */
export interface GenerationResponse {
  httpStatus: number;
  /** status field from the body: complete | accepted | rejected | noop, or undefined on error. */
  status?: string;
  url?: string;
  jobId?: string;
  /** Per-photo results for an all_images fan-out. */
  results?: Array<{ ring_image_id: string; status: string; url?: string; job_id?: string; error?: string }>;
  error?: string;
}

export async function requestGeneration(service: AuthService, body: GenerationRequest): Promise<GenerationResponse> {
  const res = await service.authFetch(`${base}/v1/generations`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => ({}))) as {
    status?: string;
    url?: string;
    job_id?: string;
    error?: string;
    results?: GenerationResponse["results"];
  };
  return {
    httpStatus: res.status,
    ...(parsed.status !== undefined ? { status: parsed.status } : {}),
    ...(parsed.url !== undefined ? { url: parsed.url } : {}),
    ...(parsed.job_id !== undefined ? { jobId: parsed.job_id } : {}),
    ...(parsed.results !== undefined ? { results: parsed.results } : {}),
    ...(parsed.error !== undefined ? { error: parsed.error } : {}),
  };
}

export interface JobState {
  status: string; // complete | rejected | in_review | pending | processing
  url?: string;
}

export async function pollJob(service: AuthService, jobId: string): Promise<JobState> {
  const res = await service.authFetch(`${base}/v1/generations/${encodeURIComponent(jobId)}`);
  if (!res.ok) throw new Error(`poll failed: ${await errorText(res)}`);
  const body = (await res.json()) as { status: string; url?: string };
  return { status: body.status, ...(body.url !== undefined ? { url: body.url } : {}) };
}
